/**
 * The site's figures in the KPI bar over the date range, each from the days of the range its source
 * has data for, and its change against as many days immediately before them, when the data covers
 * those (feature_watchdog/issues-page/refinement.md).
 */
const short = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const sumOf = (values) => values.reduce((total, value) => total + value, 0);
const signed = (value, text) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${text}`;

const ratioOf = (numerator, denominator) => (days) => {
    const below = sumOf(days.map((day) => day[denominator]));
    return below > 0 ? sumOf(days.map((day) => day[numerator])) / below : null;
};
/** Search Console's average position for a site: each day's position weighted by its impressions. */
const positionOf = (days) => {
    const ranked = days.filter((day) => day.position != null);
    const impressions = sumOf(ranked.map((day) => day.impressions));
    return impressions > 0 ? sumOf(ranked.map((day) => day.position * day.impressions)) / impressions : null;
};
const totalOf = (key) => (days) => sumOf(days.map((day) => day[key]));

const percentChange = (current, previous) => (previous > 0 ? signed(current - previous, `${Math.abs((current - previous) / previous * 100).toFixed(1)}%`) : null);
const count = { format: (value) => short.format(value), exact: (value) => Math.round(value).toLocaleString(), change: percentChange };

export const ANALYTICS = "Google Analytics";
export const SEARCH_CONSOLE = "Google Search Console";

/** `needs` is the field a day must have for the figure to read it; `daily` is the figure on one day, for the chart. */
export const KPIS = [
    { key: "sessions", label: "Organic sessions", source: ANALYTICS, color: "#6366f1", needs: "sessions", of: totalOf("sessions"), daily: (day) => day.sessions, ...count },
    { key: "clicks", label: "Clicks", source: SEARCH_CONSOLE, color: "#0ea5e9", needs: "clicks", of: totalOf("clicks"), daily: (day) => day.clicks, ...count },
    { key: "impressions", label: "Impressions", source: SEARCH_CONSOLE, color: "#14b8a6", needs: "impressions", of: totalOf("impressions"), daily: (day) => day.impressions, ...count },
    {
        key: "ctr", label: "CTR", source: SEARCH_CONSOLE, color: "#a855f7", needs: "impressions", of: ratioOf("clicks", "impressions"),
        daily: (day) => (day.impressions > 0 ? day.clicks / day.impressions : null),
        format: (value) => `${(value * 100).toFixed(1)}%`,
        exact: (value) => `${(value * 100).toFixed(2)}%`,
        change: (current, previous) => signed(current - previous, `${Math.abs((current - previous) * 100).toFixed(1)} pp`),
    },
    {
        key: "position", label: "Avg. position", source: SEARCH_CONSOLE, color: "#f97316", needs: "impressions", of: positionOf, lowerIsBetter: true,
        daily: (day) => day.position,
        format: (value) => value.toFixed(1),
        exact: (value) => value.toFixed(1),
        change: (current, previous) => signed(Math.round((current - previous) * 10), Math.abs(current - previous).toFixed(1)),
    },
];

/** Whether the figure got better from `previous` to `current`: true, false, or null when level at the precision shown. */
const betterOf = (kpi, current, previous) => {
    if (kpi.format(current) === kpi.format(previous)) return null;
    return kpi.lowerIsBetter ? current < previous : current > previous;
};

/**
 * Each of KPIS over the days of `range` in `traffic` (the fixture's site days):
 * `{ ...kpi, value, change, better, days }`, value null when the source has no day in the range,
 * change and better null when the days before don't cover it, days the range's days it has.
 */
export const kpisOf = (traffic, range) => KPIS.map((kpi) => {
    const has = (day) => day[kpi.needs] != null;
    const firstIndex = traffic.findIndex((day) => day.date >= range.from && day.date <= range.to && has(day));
    const current = traffic.filter((day) => day.date >= range.from && day.date <= range.to && has(day));
    const value = current.length ? kpi.of(current) : null;
    if (value == null) return { ...kpi, value: null, change: null, better: null, days: [] };
    const before = firstIndex >= current.length ? traffic.slice(firstIndex - current.length, firstIndex) : [];
    const previous = before.length === current.length && before.every(has) ? kpi.of(before) : null;
    return {
        ...kpi,
        value,
        change: previous == null ? null : kpi.change(value, previous),
        better: previous == null ? null : betterOf(kpi, value, previous),
        days: current,
    };
});
